// One importer per source. The importer is where all source-specific knowledge lives: where to reach
// the export, which file types it accepts, the prompt shown to the user, how to parse the file, and
// (for CSV sources) which columns map to which fields. The wizard talks only to the `Importer`
// interface and never branches on the source string. The actual parsing is delegated to the pure,
// unit-tested functions in `import-parsers.ts` — the classes orchestrate; the functions do the work.

import { detectCsvMapping, parseCsv, parseTrello, rowsFromCsv } from "./import-parsers";
import { CsvColumnMapping, ImportRow, ImportSource } from "./import-types";

// Thrown when a file parses cleanly but yields no usable rows, so the wizard can show a specific
// message ("no data rows") instead of the generic "couldn't parse" one.
export class EmptyImportError extends Error {
  constructor(message: string = "That file has no data rows.") {
    super(message);
    this.name = "EmptyImportError";
  }
}

// A column-mapped (CSV) parse also returns the grid + detected mapping so the user can re-map columns
// by hand. JSON sources (Trello) have a fixed shape and expose no remap handle.
export interface CsvRemap {
  grid: string[][];
  headers: string[];
  mapping: CsvColumnMapping;
}

// The result of parsing a source file: normalized rows, plus (CSV only) the remap handle.
export interface ParsedImport {
  rows: ImportRow[];
  remap: CsvRemap | null;
}

export abstract class Importer {
  abstract readonly source: ImportSource;
  abstract readonly label: string; // source-picker button text
  abstract readonly accept: string; // file-input `accept` attribute
  abstract readonly hint: string; // how to produce the export
  abstract readonly fileNoun: string; // dropzone prompt noun, e.g. "CSV" / "JSON"
  abstract parse(text: string): ParsedImport;
}

// Base for every spreadsheet/CSV source. Subclasses customize only metadata; a source that ever needs
// bespoke column detection can override `detectMapping`.
export class CsvImporter extends Importer {
  readonly source: ImportSource = "csv";
  readonly label: string = "CSV";
  readonly accept: string = ".csv,text/csv";
  readonly hint: string = "Any spreadsheet exported as CSV. Map the columns below.";
  readonly fileNoun: string = "CSV";

  parse(text: string): ParsedImport {
    const grid: string[][] = parseCsv(text);
    if (grid.length < 2) throw new EmptyImportError();
    const headers: string[] = grid[0];
    const mapping: CsvColumnMapping = this.detectMapping(headers);
    return { rows: rowsFromCsv(grid, mapping), remap: { grid, headers, mapping } };
  }

  // Re-derive rows after the user edits the column mapping in the wizard.
  applyMapping(grid: string[][], mapping: CsvColumnMapping): ImportRow[] {
    return rowsFromCsv(grid, mapping);
  }

  protected detectMapping(headers: string[]): CsvColumnMapping {
    return detectCsvMapping(headers);
  }
}

export class JiraImporter extends CsvImporter {
  readonly source: ImportSource = "jira";
  readonly label: string = "Jira";
  readonly hint: string = "Jira → your project's Issues → Export → CSV (current fields).";
}

export class LinearImporter extends CsvImporter {
  readonly source: ImportSource = "linear";
  readonly label: string = "Linear";
  readonly hint: string = "Linear → Settings → Import / Export → Export CSV.";
}

export class AsanaImporter extends CsvImporter {
  readonly source: ImportSource = "asana";
  readonly label: string = "Asana";
  readonly hint: string =
    "Asana → project ▾ menu → Export / Print → CSV. (Asana doesn't export sections — pick a status below.)";
}

export class BasecampImporter extends CsvImporter {
  readonly source: ImportSource = "basecamp";
  readonly label: string = "Basecamp";
  readonly hint: string =
    "Basecamp has no built-in CSV export — export your to-dos to CSV, then map the columns below.";
}

export class TrelloImporter extends Importer {
  readonly source: ImportSource = "trello";
  readonly label: string = "Trello";
  readonly accept: string = ".json,application/json";
  readonly hint: string = "Trello → board menu → … More → Print and Export → Export as JSON.";
  readonly fileNoun: string = "JSON";

  parse(text: string): ParsedImport {
    return { rows: parseTrello(text), remap: null };
  }
}

// Registry in display order — the wizard renders one source button per entry.
export const IMPORTERS: Importer[] = [
  new JiraImporter(),
  new TrelloImporter(),
  new LinearImporter(),
  new AsanaImporter(),
  new BasecampImporter(),
  new CsvImporter()
];

const BY_SOURCE: Map<ImportSource, Importer> = new Map(IMPORTERS.map((imp: Importer) => [imp.source, imp]));

export function getImporter(source: ImportSource): Importer {
  const importer: Importer | undefined = BY_SOURCE.get(source);
  if (!importer) throw new Error(`No importer registered for source '${source}'.`);
  return importer;
}
